import { Cable, Channel, createCable } from "@anycable/web";
import * as Sentry from "@sentry/react";
import { notification } from "antd";
import { useEffect, useState } from "react";
import { baseUrlAltSubdomain } from "../components/Helper/Url";
import { globalAppConfig } from "../config";
import { CalendarEvent } from "../models/CalendarEvent";

interface useAnyCableProps {
    projectInfo: any | null;
    reloadCurrentProject: () => void;
    currentTeamsProjects: any | null;
    setNewBroadcastMessage: (value: boolean) => void;
    updateCalendarEventInState: (projectToken: string, calendarEvent: any) => void;
    calendarEventsByProject: { unique_token: string; calendarEvents: any[] }[];
}

const useAnyCable = (props: useAnyCableProps) => {
    const [cableApp, setCableApp] = useState<Cable | null>(null);
    const [dashboardNotificationsChannel, setDashboardNotificationsChannel] = useState<Channel | null>(null);
    const [mapChannel, setMapChannel] = useState<Channel | null>(null);
    const [calendarEventsChannel, setCalendarEventsChannel] = useState<Channel | null>(null);
    const [broadcastsChannel, setBroadcastsChannel] = useState<Channel | null>(null);
    const [procurementManualSyncChannel, setProcurementManualSyncChannel] = useState<Channel | null>(null);

    useEffect(() => {
        const appUrl = baseUrlAltSubdomain("app") + "/cable";
        const cableUrl = appUrl.replace("https://", "wss://").replace("http://", "ws://");

        setCableApp(createCable(cableUrl));

        return () => {
            try {
                dashboardNotificationsChannel?.disconnect();
                mapChannel?.disconnect();
                broadcastsChannel?.disconnect();
                cableApp?.disconnect();
            } catch (error) {
                Sentry.captureException(error);
            }
        };
    }, []);

    useEffect(() => {
        if (!cableApp || !props.projectInfo) return;

        createMapChannel();
        createDashboardNotificationsChannel();
        createBroadcastChannel();
        createProcurementManualSyncChannel();
    }, [cableApp, props.projectInfo]);

    const createCalendarEventsChannel = () => {
        if (!cableApp || !props.projectInfo || !props.calendarEventsByProject) {
            return;
        }

        if (calendarEventsChannel) {
            try {
                calendarEventsChannel.disconnect();
            } catch (error) {
                Sentry.captureException(error);
            }
            setCalendarEventsChannel(null);
        }

        const newCalendarEventsChannel = cableApp.subscribeTo("CalendarEventsChannel", {
            project_unique_token: props.projectInfo.unique_token,
        });

        newCalendarEventsChannel.on("message", (data: any) => {
            if (data.method == "update" && data.type == "calendar_event") {
                const calendarEvent = CalendarEvent.fromApiObject(data.calendar_event, props.projectInfo.timezone);

                props.updateCalendarEventInState(data.project_unique_token, calendarEvent);
            }
        });

        newCalendarEventsChannel.on("disconnect", (data) => {
            setCalendarEventsChannel(null);
        });

        setCalendarEventsChannel(newCalendarEventsChannel);
    };

    useEffect(() => {
        createCalendarEventsChannel();
    }, [props.projectInfo, props.calendarEventsByProject, cableApp]);

    const createMapChannel = () => {
        if (mapChannel) {
            try {
                mapChannel.disconnect();
            } catch (error) {
                Sentry.captureException(error);
            }
            setMapChannel(null);
        }

        const newMapChannel = cableApp?.subscribeTo("MapChannel", {
            project_unique_token: props.projectInfo.unique_token,
        });

        newMapChannel?.on("message", (data: any) => {
            if (data.method == "update" && data.action == "reload_project") {
                props.reloadCurrentProject();
            }
        });

        newMapChannel?.on("disconnect", (data) => {
            setMapChannel(null);
        });

        setMapChannel(newMapChannel || null);
    };

    const createDashboardNotificationsChannel = () => {
        if (dashboardNotificationsChannel) {
            try {
                dashboardNotificationsChannel.disconnect();
            } catch (error) {
                Sentry.captureException(error);
            }
        }

        const newDashboardNotificationsChannel = cableApp?.subscribeTo("DashboardNotificationsChannel");

        newDashboardNotificationsChannel?.on("disconnect", (data) => {
            setDashboardNotificationsChannel(null);
        });

        setDashboardNotificationsChannel(newDashboardNotificationsChannel || null);
    };

    const createBroadcastChannel = () => {
        if (broadcastsChannel) {
            try {
                broadcastsChannel.disconnect();
            } catch (error) {
                Sentry.captureException(error);
            }
            setBroadcastsChannel(null);
        }
        const newBroadcastsChannel = cableApp?.subscribeTo("BroadcastsChannel", {
            project_unique_token: props.projectInfo.unique_token,
        });
        if (newBroadcastsChannel) {
            newBroadcastsChannel?.on("message", (data) => {
                props.setNewBroadcastMessage(true);
                notification.warning({
                    message: `New Jobsite Alert from ${data.user.fullname}`,
                    description: data.message.body,
                    duration: globalAppConfig.NOTIFICATION_DURATION,
                    placement: "topLeft",
                });
            });
        }

        newBroadcastsChannel?.on("disconnect", (data) => {
            setBroadcastsChannel(null);
        });

        setBroadcastsChannel(newBroadcastsChannel || null);
    };

    const createProcurementManualSyncChannel = () => {
        if (procurementManualSyncChannel) {
            try {
                procurementManualSyncChannel.disconnect();
            } catch (error) {
                Sentry.captureException(error);
            }
            setProcurementManualSyncChannel(null);
        }

        const newProcurementManualSyncChannel = cableApp?.subscribeTo("ProcurementManualSyncChannel", {
            project_unique_token: props.projectInfo.unique_token,
        });

        newProcurementManualSyncChannel?.on("message", (data) => {
            if (data.status == "completed") {
                props.reloadCurrentProject();
                notification.success({
                    message: `Procurement Sync Completed`,
                    description: `The procurement sync has completed.`,
                    duration: globalAppConfig.NOTIFICATION_DURATION,
                });
            } else if (data.status == "retrying") {
                notification.warning({
                    message: `Procurement Sync Failed`,
                    description: `The procurement sync is being retried in the background.`,
                    duration: globalAppConfig.NOTIFICATION_DURATION,
                });
            }
        });

        newProcurementManualSyncChannel?.on("disconnect", (data) => {
            setProcurementManualSyncChannel(null);
        });

        setProcurementManualSyncChannel(newProcurementManualSyncChannel || null);
    };

    return {
        cableApp,
        dashboardNotificationsChannel,
        mapChannel,
    };
};

export default useAnyCable;
